import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import type { ConsentPreferences } from "@/lib/utils/consent";
import { useLegalTranslation } from "@/hooks";
import { COOKIE_CATEGORY_KEYS, type CookieCategoryKey } from "@/lib/constants/legal";

interface CookiePreferencesModalProps {
  open: boolean;
  initialPreferences: ConsentPreferences;
  onOpenChange: (open: boolean) => void;
  onSaveCustom: (preferences: ConsentPreferences) => void;
  onAcceptAll: () => void;
  onRejectAll: () => void;
}

export function CookiePreferencesModal({
  open,
  initialPreferences,
  onOpenChange,
  onSaveCustom,
  onAcceptAll,
  onRejectAll,
}: CookiePreferencesModalProps) {
  const { t } = useLegalTranslation();
  const [analyticsEnabled, setAnalyticsEnabled] = useState(initialPreferences.analytics);
  const [marketingEnabled, setMarketingEnabled] = useState(initialPreferences.marketing);

  useEffect(() => {
    if (!open) return;
    setAnalyticsEnabled(initialPreferences.analytics);
    setMarketingEnabled(initialPreferences.marketing);
  }, [open, initialPreferences]);

  const handleSave = () => {
    onSaveCustom({
      necessary: true,
      analytics: analyticsEnabled,
      marketing: marketingEnabled,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("cookiePreferences.title")}</DialogTitle>
          <DialogDescription>{t("cookiePreferences.description")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {COOKIE_CATEGORY_KEYS.map((categoryKey) => (
            <CookieCategoryRow
              key={categoryKey}
              title={t(`cookiePreferences.categories.${categoryKey}.title`)}
              description={t(`cookiePreferences.categories.${categoryKey}.description`)}
              checked={resolveCategoryValue(categoryKey, analyticsEnabled, marketingEnabled)}
              disabled={categoryKey === "necessary"}
              onCheckedChange={buildCategoryHandler(categoryKey, setAnalyticsEnabled, setMarketingEnabled)}
            />
          ))}
        </div>

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={onRejectAll}>
            {t("cookieBanner.rejectButton")}
          </Button>
          <Button type="button" variant="outline" onClick={onAcceptAll}>
            {t("cookieBanner.acceptAllButton")}
          </Button>
          <Button type="button" onClick={handleSave}>
            {t("cookiePreferences.saveButton")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface CookieCategoryRowProps {
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onCheckedChange: (checked: boolean) => void;
}

function CookieCategoryRow({ title, description, checked, disabled = false, onCheckedChange }: CookieCategoryRowProps) {
  return (
    <div className="rounded-md border border-gray-200 p-3">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <p className="text-sm font-semibold text-gray-900">{title}</p>
          <p className="text-xs text-gray-600">{description}</p>
        </div>
        <Switch checked={checked} onCheckedChange={onCheckedChange} disabled={disabled} aria-label={title} />
      </div>
    </div>
  );
}

function resolveCategoryValue(categoryKey: CookieCategoryKey, analyticsEnabled: boolean, marketingEnabled: boolean): boolean {
  if (categoryKey === "necessary") return true;
  if (categoryKey === "analytics") return analyticsEnabled;
  return marketingEnabled;
}

function buildCategoryHandler(
  categoryKey: CookieCategoryKey,
  setAnalyticsEnabled: (checked: boolean) => void,
  setMarketingEnabled: (checked: boolean) => void
): (checked: boolean) => void {
  if (categoryKey === "analytics") return setAnalyticsEnabled;
  if (categoryKey === "marketing") return setMarketingEnabled;
  return () => {};
}
